/**
 * drumHead3DPipeline — a 3D drum viewed from a low angle, with the
 * drumhead as a circular SDF disk. Bass pulses launch visible wave
 * packets across the head, mid adds harmonic secondary modes, and
 * the wooden shell is hinted at with a side-stripe color band.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface DrumHead3DPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}

float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1, 0));
  float c = hash21(i + vec2(0, 1));
  float d = hash21(i + vec2(1, 1));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

// Map the entire drum — cylinder shell with disk head on top.
// Cylinder SDF (capped at top and bottom).
float sdCappedCylinder(vec3 p, float h, float r) {
  vec2 d = abs(vec2(length(p.xz), p.y)) - vec2(r, h);
  return min(max(d.x, d.y), 0.0) + length(max(d, 0.0));
}

// Drum head height field as a separate SDF component.
float sdDrumHead(vec3 p) {
  float r = length(p.xy);
  if (r > 1.05) return 1.0;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  mid = pow(clamp(mid * 2.6, 0.0, 1.0), 0.8);
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);
  float t = uTime;
  // Three orbital strike points.
  vec2 s1 = 0.5 * vec2(cos(t * 0.5), sin(t * 0.42));
  vec2 s2 = 0.5 * vec2(cos(t * 0.37 + 2.1), sin(t * 0.55));
  vec2 s3 = 0.5 * vec2(cos(t * 0.61 - 1.7), sin(t * 0.33 + 0.9));
  float h = 0.0;
  // Wave packets expanding from each strike.
  h += sin((r - t * 1.5 + length(p.xy - s1)) * 8.0) * exp(-length(p.xy - s1) * 2.0) * uBass.x * 0.10 * bass;
  h += sin((r - t * 1.4 + length(p.xy - s2)) * 9.0) * exp(-length(p.xy - s2) * 2.0) * uBass.y * 0.10 * bass;
  h += sin((r - t * 1.3 + length(p.xy - s3)) * 7.5) * exp(-length(p.xy - s3) * 2.0) * uBass.z * 0.10 * bass;
  // Standing wave — azimuthal Bessel-like mode.
  h += sin(r * 14.0 - t * 8.0) * exp(-r * 1.5) * mid * 0.04;
  // High-frequency micro shimmer.
  h += sin(p.x * 30.0 + t * 3.0) * sin(p.y * 28.0 - t * 2.5) * 0.003 * high;
  // Shell of the drum constrains the head below r = 1.0.
  float z = h; // head surface at z = h
  return p.z - z - 0.5;
}

float mapDrum(vec3 p) {
  // Cylinder shell at z = 0..-0.6.
  vec3 cp = p - vec3(0.0, -0.3, 0.0);
  float body = sdCappedCylinder(cp, 0.3, 1.0);
  // Head surface just above z = 0.5.
  float head = sdDrumHead(p - vec3(0.0, 0.0, 0.5));
  return min(body, head);
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.0015, 0.0);
  return normalize(vec3(
    mapDrum(p + e.xyy) - mapDrum(p - e.xyy),
    mapDrum(p + e.yxy) - mapDrum(p - e.yxy),
    mapDrum(p + e.yyx) - mapDrum(p - e.yyx)
  ));
}

void main() {
  vec2 uv = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  uv.x *= uResolution.x / uResolution.y;
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.4, 0.0, 1.0), 0.65);
  mid = pow(clamp(mid * 2.6, 0.0, 1.0), 0.8);
  high = pow(clamp(high * 3.6, 0.0, 1.0), 0.85);

  // Camera looking down and slightly tilted.
  vec3 ro = vec3(2.2, 1.6, 1.8);
  vec3 ww = normalize(vec3(0.0, 0.0, 0.2) - ro);
  vec3 uu = normalize(cross(ww, vec3(0.0, 0.0, 1.0)));
  vec3 vv = cross(uu, ww);
  vec3 rd = normalize(uv.x * uu + uv.y * vv + 1.7 * ww);

  float t = 0.0;
  bool hit = false;
  vec3 p = ro;
  for (int i = 0; i < 120; i++) {
    float d = mapDrum(p);
    if (d < 0.0008) { hit = true; break; }
    if (t > 9.0) break;
    t += d * 0.9;
    p = ro + rd * t;
  }
  vec3 col = vec3(0.025, 0.02, 0.035);
  if (hit) {
    vec3 n = calcNormal(p);
    vec3 ldir = normalize(vec3(0.5, 0.7, 0.6));
    float diff = clamp(dot(n, ldir), 0.0, 1.0);
    float fres = pow(1.0 - clamp(dot(n, -rd), 0.0, 1.0), 2.5);
    vec3 h = normalize(ldir - rd);
    float spec = pow(clamp(dot(n, h), 0.0, 1.0), 32.0);
    // Determine which part of the drum we hit.
    bool isHead = p.z > 0.45;
    col = vec3(0.0);
    if (isHead) {
      // Drum head — bright translucent membrane.
      col += diff * vec3(0.85, 0.78, 0.7);
      // Concentric mode rings.
      float rings = 0.5 + 0.5 * sin(length(p.xy - vec2(0.0, 0.0)) * 18.0 - uTime * 5.0);
      col += vec3(0.85, 0.55, 0.95) * pow(rings, 4.0) * (0.25 + bass * 0.6);
      col += fres * vec3(0.6, 0.8, 1.0) * (0.4 + high * 0.8);
      col += spec * vec3(1.0) * (0.3 + bass * 0.5);
    } else {
      // Shell — wood grain.
      float grain = valueNoise(vec2(atan(p.x, p.z) * 6.0, p.y * 5.0));
      vec3 wood = mix(vec3(0.32, 0.18, 0.10), vec3(0.55, 0.32, 0.18), grain);
      col += wood * (0.3 + diff * 0.7);
      col += fres * vec3(0.85, 0.55, 0.35) * (0.2 + mid * 0.4);
      col += spec * vec3(1.0) * (0.2 + high * 0.3);
      // Decorative band.
      float band = smoothstep(0.05, 0.0, abs(p.y + 0.05));
      col = mix(col, vec3(0.7, 0.15, 0.15), band * 0.6);
    }
  }

  float vig = pow(16.0 * vUV.x * (1.0 - vUV.x) * vUV.y * (1.0 - vUV.y), 0.25);
  col *= vig;
  col = col / (1.0 + col);
  col = pow(col, vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}`;

export function computeDrumHead3DBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.95);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.85));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.6);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.15 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.75 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.6 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 4.0 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 6.0 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 8.0 + 2.1);
}

export function computeDrumHead3DRealtimeBands(freqData: Uint8Array, out: Float32Array): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.008], [0.008, 0.030], [0.030, 0.060],
    [0.060, 0.150], [0.150, 0.260], [0.260, 0.420],
    [0.420, 0.620], [0.620, 0.800], [0.800, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    let s = 0, c = 0;
    const a = Math.floor(lo * N);
    const b = Math.floor(hi * N);
    for (let k = a; k < b; k++) { s += freqData[k]; c++; }
    out[i] = c > 0 ? (s / c) / 255 : 0;
  }
}

export function createDrumHead3DPipeline(gl: WebGL2RenderingContext): DrumHead3DPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawDrumHead3D(
  gl: WebGL2RenderingContext,
  pipe: DrumHead3DPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}